import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DeviceFactory } from './device-factory/device-factory';

type Device = {
	application_type: number | null,
	sct_device_type: number
}

type SiteBattlinkCounts = {
	battlink_devices_count?: number | string,
	lithium_battlink_devices_count?: number | string,
	battlink_ii_devices_count?: number | string,
}

@Injectable({
	providedIn: 'root'
})
export class DeviceTypeService {
	readonly types = {
		iotah: 0,
		charglink: 1,
		battlink: 2,
		lithiumBattlink: 5,
		battlinkII: 6,
	};

	readonly typesString = {
		iotah: 'Truklink', 
		charglink: 'ChargLink',
		study: 'Study',
	};

	readonly options = [
		{id: this.types.iotah, name: this.translateService.instant('g.iotah')},
		{id: this.types.charglink, name: this.translateService.instant('g.charglink')},
		{id: this.types.battlink, name: this.translateService.instant('g.battlink')},
		{id: this.types.lithiumBattlink, name: this.translateService.instant('g.lithium_battlink')},
		{id: this.types.battlinkII, name: this.translateService.instant('g.battlink_ii')},
	];

	readonly translationPath: {[key: number]: string} = {
		[this.types.iotah]: "",
		[this.types.charglink]: "chargLinkDevice.",
		[this.types.battlink]: "battlink.",
		[this.types.lithiumBattlink]: "lithiumBattlinkDevice.",
		[this.types.battlinkII]: "battlink.",
	};

	private readonly imageFolders: {[key: number]: string} = {
		[this.types.iotah]: "/forklift",
		[this.types.charglink]: "/chargLink/charger"
	};

	private readonly studyDeviceStyleClass = 'study-logo';
	private readonly logoStyleClass: {[key: number]: string} = {
		[this.types.charglink]: "charglink-logo"
	};

	readonly reportKeysMapper = {
		[this.types.iotah]: {
			period: 'months',
			device: 'iotah',
		},
		[this.types.charglink]: {
			period: 'quarter',
			device: 'chargLink',
		}
	}

	mapColor = {
		[this.types.iotah]: "orange",
		[this.types.charglink]: "#a5d662"
	}

	readonly applicationTypes = {
		FORKLIFT: 0,
		POWER_STUDIES: 1,
		GOLF_CART: 2,
		BOAT: 3
	};

	readonly iotemeterDeviceTypes: number[] = DeviceFactory.ImeterOptionsType.map(o => o.id);

	constructor(private translateService: TranslateService) { }

	isIotemeterDevice(deviceOrType: Device | number): boolean {
		const deviceType = typeof deviceOrType === 'number' ? deviceOrType : deviceOrType.sct_device_type;
		return this.iotemeterDeviceTypes.includes(deviceType);
	}

	isIotah(deviceOrType: Device | number): boolean {
		return this.isDeviceType(deviceOrType, this.types.iotah);
	}

	isCharglink(deviceOrType: Device | number): boolean {
		return this.isDeviceType(deviceOrType, this.types.charglink);
	}

	isBattLink(deviceOrType: Device | number): boolean {
		return this.isDeviceType(deviceOrType, this.types.battlink);
	}

	isLithiumBattLink(deviceOrType: Device | number): boolean {
		return this.isDeviceType(deviceOrType, this.types.lithiumBattlink);
	}

	isBattLinkII(deviceOrType: Device | number): boolean {
		return this.isDeviceType(deviceOrType, this.types.battlinkII);
	}

	// A site is lithium-only when it has Lithium BATTlinks and no lead-acid BATTlink / BATTlink II.
	// Other device types (Truklink, ChargLink, BRM, ioTEmeter) don't affect the result.
	isLithiumOnlyBattlinkSite(site: SiteBattlinkCounts | null | undefined): boolean {
		if (!site)
			return false;

		const lithiumCount = +site.lithium_battlink_devices_count || 0;
		const leadAcidCount = (+site.battlink_devices_count || 0) + (+site.battlink_ii_devices_count || 0);

		return lithiumCount > 0 && leadAcidCount == 0;
	}

	getTypesString(device: Device, options: {getPowerStudyDevices: boolean} = {getPowerStudyDevices: false}) {
		if (options.getPowerStudyDevices && this.isStudyDevice(device))
			return this.typesString.study;

		if (this.isIotah(device))
			return this.typesString.iotah;

		if (this.isCharglink(device))
			return this.typesString.charglink;
	}

	group(devices: Device[], options: {getPowerStudyDevices: boolean} = {getPowerStudyDevices: false}) {
		const returnValue: {[key: string]: any[]} = {
			iotahDevices: [],
			charglinkdDevices: []
		};

		if (options.getPowerStudyDevices)
			returnValue.iotahPsDevices = [];

		for (const device of devices) {
			if (this.isStudyDevice(device) && options.getPowerStudyDevices)
				returnValue.iotahPsDevices.push(device);

			else if (this.isCharglink(device))
				returnValue.charglinkdDevices.push(device);

			else
				returnValue.iotahDevices.push(device);
		}

		return returnValue;
	}

	getKeyForReportSource(device: {sct_device_type: number}, source: string) {
		return this.reportKeysMapper[device.sct_device_type][source];
	}

	getMapColor(device: Device) {
		return this.mapColor[device.sct_device_type];
	}

	imagePacker(imageType: 'not_connected' | 'connected' | 'inuse' | 'idle', device: Device) {
		let imageLink = '/images';
		imageLink += this.imageFolders[device.sct_device_type];

		switch (imageType) {
			case "not_connected":
				imageLink += `_gray${this.isCharglink(device) ? '_v2' : '' }.png`;
				if (this.isStudyDevice(device))
					imageLink = 'images/studies/not_connected.png';
				break;
			case "connected":
				imageLink += `_connected${this.isCharglink(device) ? '_v2' : '' }.png`;
				if (this.isStudyDevice(device))
					imageLink = 'images/studies/connected.png';
				break;
			case "inuse":
				imageLink = '/images/forklift_inuse.gif';
				break;
			case "idle":
				imageLink = '/images/forklift_idle.png';
				break;
		}
		return imageLink;
	}

	getLogoClasses(device: Device): string {
		const classesList = [];

		if (this.isCharglink(device))
			classesList.push(this.logoStyleClass[this.types.charglink]);

		if (this.isStudyDevice(device))
			classesList.push(this.studyDeviceStyleClass);

		return classesList.join(' ');
	}

	translate(key: string, deviceOrType: Device | number): string {
		const translationKey = this.getTranslationKey(key, deviceOrType);
		return this.translateService.instant(translationKey);
	}

	getTranslationKey(key: string, deviceOrType: Device | number): string {
		let translationKey = key;
		if (this.isCharglink(deviceOrType))
			translationKey = `${this.translationPath[this.types.charglink]}${key}`;

		if (this.isBattLink(deviceOrType))
			translationKey = `${this.translationPath[this.types.battlink]}${key}`;

		if (this.isLithiumBattLink(deviceOrType))
			translationKey = `${this.translationPath[this.types.lithiumBattlink]}${key}`;

		if (this.isBattLinkII(deviceOrType))
			translationKey = `${this.translationPath[this.types.battlinkII]}${key}`;

		return translationKey;
	}

	private isDeviceType(
		deviceOrType: Device | number,
		targetType: number
	): boolean {
		if (typeof deviceOrType === 'number') {
			return deviceOrType === targetType;
		} else {
			return deviceOrType.sct_device_type === targetType;
		}
	}

	isStudyDevice(device: Device): boolean {
		return device.application_type == DeviceFactory.applicationTypes.POWER_STUDIES && DeviceFactory.createDevice(device.sct_device_type).config.isIotah;
	}
}
